import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api/client";
import Logo from "../../components/Logo";
import { hashPassword } from "../../api/crypto";

function passwordStrength(pwd) {
  if (!pwd) return null;
  let score = 0;
  if (pwd.length >= 8)  score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  if (score <= 1) return { label: "Muy débil", color: "#ef4444", pct: 20 };
  if (score === 2) return { label: "Débil",     color: "#f97316", pct: 40 };
  if (score === 3) return { label: "Media",     color: "#eab308", pct: 60 };
  if (score === 4) return { label: "Fuerte",    color: "#22c55e", pct: 80 };
  return                 { label: "Muy fuerte", color: "#16a34a", pct: 100 };
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [form, setForm] = useState({ password: "", confirmar: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [exito, setExito] = useState(false);

  useEffect(() => {
    if (!token) setError("Enlace inválido. Solicita uno nuevo.");
  }, [token]);

  const errPwd = !form.password
    ? "La contraseña es obligatoria"
    : form.password.length < 8
    ? "Mínimo 8 caracteres"
    : "";

  const errConfirmar = !form.confirmar
    ? "Confirma tu contraseña"
    : form.confirmar !== form.password
    ? "Las contraseñas no coinciden"
    : "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (errPwd || errConfirmar) return;
    setError("");
    setLoading(true);
    try {
      const hashed = await hashPassword(form.password);
      await api.post("/auth/restablecer-password", { token, password: hashed });
      setExito(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al restablecer la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  const strength = passwordStrength(form.password);

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg, #0A1128 0%, #1a1f4e 60%, #0A1128 100%)" }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="flex flex-col items-center mb-8">
          <Logo width={200} />
          <h1 className="text-lg font-bold text-gray-900 mt-3">Nueva contraseña</h1>
          <p className="text-gray-500 text-sm mt-1">Elige una contraseña segura para tu cuenta.</p>
        </div>

        {exito ? (
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mx-auto">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-gray-800 font-semibold">¡Contraseña actualizada!</p>
              <p className="text-gray-500 text-sm mt-1">
                Tu contraseña fue cambiada exitosamente. Redirigiendo al inicio de sesión...
              </p>
            </div>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm mb-4">
                {error}
                {!token && (
                  <span>
                    {" "}
                    <Link to="/olvide-password" className="underline font-medium">
                      Solicitar nuevo enlace
                    </Link>
                  </span>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              {/* Nueva contraseña */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nueva contraseña
                </label>
                <div className="rounded-lg border border-gray-300 flex items-center">
                  <input
                    type={showPwd ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                    autoComplete="new-password"
                    placeholder="Mínimo 8 caracteres"
                    className="flex-1 px-3 py-2.5 text-sm focus:outline-none bg-transparent rounded-l-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="px-3 text-gray-400 hover:text-gray-600 text-xs font-medium"
                  >
                    {showPwd ? "Ocultar" : "Ver"}
                  </button>
                </div>
                {form.password && strength && (
                  <div className="mt-1.5">
                    <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{ width: `${strength.pct}%`, background: strength.color }}
                      />
                    </div>
                    <p className="text-xs mt-0.5 font-medium" style={{ color: strength.color }}>
                      {strength.label}
                    </p>
                  </div>
                )}
              </div>

              {/* Confirmar contraseña */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmar contraseña
                </label>
                <div
                  className={`rounded-lg border flex items-center transition-colors ${
                    form.confirmar && !errConfirmar
                      ? "border-green-400"
                      : "border-gray-300"
                  }`}
                >
                  <input
                    type={showConfirm ? "text" : "password"}
                    value={form.confirmar}
                    onChange={(e) => setForm((p) => ({ ...p, confirmar: e.target.value }))}
                    autoComplete="new-password"
                    placeholder="Repite tu contraseña"
                    className="flex-1 px-3 py-2.5 text-sm focus:outline-none bg-transparent rounded-l-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="px-3 text-gray-400 hover:text-gray-600 text-xs font-medium"
                  >
                    {showConfirm ? "Ocultar" : "Ver"}
                  </button>
                </div>
                {form.confirmar && !errConfirmar && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <span>✓</span> Las contraseñas coinciden
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !token || !!errPwd || !!errConfirmar}
                className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm mt-2"
              >
                {loading ? "Guardando..." : "Cambiar contraseña"}
              </button>
            </form>

            <p className="text-center text-sm text-gray-500 mt-6">
              <Link to="/login" className="text-indigo-600 font-medium hover:underline">
                ← Volver al inicio de sesión
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
